import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getUserPartsCount } from "../store/auth/authActions";

const FREE_PLAN_PARTS_LIMIT = 10;

export function usePartsQuota() {
  const dispatch = useDispatch();
  const { user, currentSubscription, userPartsCount } = useSelector(
    (state) => state.auth,
  );
  const totalParts = userPartsCount || 0;

  // A seller with no paid subscription (or plan === "free") is a valid free-tier user.
  const isSeller = user?.role === "seller";
  const isActivePaidPlan = currentSubscription?.status === "active";
  const isFreePlan = isSeller && !isActivePaidPlan;

  // Resolve the effective parts limit:
  //   - active paid subscription → use quota from subscription
  //   - free plan (no subscription) → 10 parts
  const partsLimit = isActivePaidPlan
    ? currentSubscription?.quota?.partsLimit
    : isFreePlan
      ? FREE_PLAN_PARTS_LIMIT
      : 0;

  let canAddParts = false;
  let remaining = 0;

  useEffect(() => {
    if (user?.uid && currentSubscription?.currentPeriodStart) {
      dispatch(
        getUserPartsCount({
          userId: user.uid,
          startDate: currentSubscription.currentPeriodStart,
        }),
      );
    } else if (user?.uid && isFreePlan) {
      // For free plan, count all parts ever added (no period restriction)
      dispatch(getUserPartsCount({ userId: user.uid }));
    }
  }, []);

  if (isActivePaidPlan) {
    if (partsLimit === "unlimited") {
      canAddParts = true;
      remaining = Infinity;
    } else if (!isNaN(Number(partsLimit))) {
      remaining = Number(partsLimit) - totalParts;
      canAddParts = remaining > 0;
    }
  } else if (isFreePlan) {
    remaining = FREE_PLAN_PARTS_LIMIT - totalParts;
    canAddParts = remaining > 0;
  }

  return {
    canAddParts,
    partsLimit,
    totalParts,
    remaining,
  };
}
